import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

import type { TenantConfig } from '@/config/app/types'

import type { RoleEnum } from '@/shared/api/intranet'
import { getAndClearRedirectUrl, saveRedirectUrl } from '@/shared/lib/router'
import { canAccessColleaguesList, resolveFeatureFlag } from '@/shared/lib/tenant'

import { useProfile } from '@/entities/profile'

/**
 * Проверяет, является ли путь публичным (не требует авторизации)
 */
function isPublicPath(path: string): boolean {
  const publicPaths = ['/auth/sign-in', '/auth/sign-in-status', '/privacy-policy', '/500', '/test']
  return publicPaths.some((publicPath) => path.startsWith(publicPath))
}

/**
 * Проверяет права доступа по ролям
 */
function checkRoleAccess(to: RouteLocationNormalized, userRole: string): string | null {
  if (to.meta.adminRoute && userRole !== 'ADMIN') {
    return '/'
  }

  if (to.meta.shopManagerRoute && !['SHOP_MANAGER', 'ADMIN'].includes(userRole)) {
    return '/'
  }

  if (to.meta.contentManagerRoute && !['CONTENT_MANAGER', 'ADMIN'].includes(userRole)) {
    return '/'
  }

  return null
}

/**
 * Проверяет доступ для пользователей на испытательном сроке
 */
function checkProbationAccess(path: string, isProbationPassed: boolean): string | null {
  const probationBlockedRoutes = ['/shop', '/my-orders', '/events']
  if (!isProbationPassed && probationBlockedRoutes.some((route) => path.startsWith(route))) {
    return '/'
  }
  return null
}

/**
 * Проверяет доступ к списку сотрудников по feature flag и роли
 */
function checkColleaguesListAccess(path: string, config: TenantConfig, role: RoleEnum): string | null {
  if (!path.startsWith('/profiles')) {
    return null
  }

  const canViewColleaguesList = resolveFeatureFlag(config, 'canViewColleaguesList')
  const hasAccess = canAccessColleaguesList({
    canViewColleaguesList,
    role,
  })

  if (!hasAccess) {
    return '/'
  }

  return null
}

/**
 * Guard для проверки авторизации пользователя
 */
export async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  config: TenantConfig,
) {
  const profileStore = useProfile()
  const { isInitialized, profile, isProbationPassed } = storeToRefs(profileStore)

  if (isPublicPath(to.path)) {
    return next()
  }

  if (!isInitialized.value) {
    await profileStore.initProfile()
  }

  if (!profile.value) {
    saveRedirectUrl(to.fullPath)
    return next('/auth/sign-in')
  }

  if (to.path.startsWith('/auth')) {
    const redirectUrl = getAndClearRedirectUrl()
    return next(redirectUrl || '/')
  }

  const roleRedirect = checkRoleAccess(to, profile.value.role)
  if (roleRedirect) {
    return next(roleRedirect)
  }

  const colleaguesListRedirect = checkColleaguesListAccess(to.path, config, profile.value.role)
  if (colleaguesListRedirect) {
    return next(colleaguesListRedirect)
  }

  const probationRedirect = checkProbationAccess(to.path, isProbationPassed.value)
  if (probationRedirect) {
    return next(probationRedirect)
  }

  next()
}
